function segmentGraphemes(input: string): string[] {
  if (!input) return []

  const IntlWithSegmenter = Intl as typeof Intl & {
    Segmenter?: new (
      locales?: string | string[],
      options?: { granularity?: "grapheme" | "word" | "sentence" }
    ) => { segment: (value: string) => Iterable<{ segment: string }> }
  }

  if (typeof IntlWithSegmenter.Segmenter === "function") {
    const segmenter = new IntlWithSegmenter.Segmenter(undefined, {
      granularity: "grapheme",
    })
    return Array.from(segmenter.segment(input), (item) => item.segment)
  }

  // Fallback for environments without Intl.Segmenter.
  return Array.from(input)
}

function firstGrapheme(input: string): string {
  return segmentGraphemes(input)[0] ?? ""
}

function firstTwoGraphemes(input: string): string {
  return segmentGraphemes(input).slice(0, 2).join("")
}

function firstNGraphemes(input: string, count: number): string {
  return segmentGraphemes(input).slice(0, count).join("")
}

function hasLetterOrDigit(input: string): boolean {
  return /\p{L}|\p{N}/u.test(input)
}

export function getAvatarInitials(
  name?: string | null,
  fallback = "?",
  maxGraphemes = 2
): string {
  const safeMax = Math.max(1, Math.floor(maxGraphemes))
  const normalized = (name ?? "").trim().replace(/^@+/, "")
  if (!normalized) return fallback

  const words = normalized.split(/\s+/).filter(Boolean)
  if (words.length >= 2 && safeMax >= 2) {
    const first = firstGrapheme(words[0])
    const second = firstGrapheme(words[1])
    const joined = `${first}${second}`.toUpperCase()
    return hasLetterOrDigit(joined) ? joined : fallback
  }

  const initials =
    safeMax === 2
      ? firstTwoGraphemes(words[0]).toUpperCase()
      : firstNGraphemes(words[0], safeMax).toUpperCase()
  return hasLetterOrDigit(initials) ? initials : fallback
}
